import React, { useEffect, useRef, useState } from "react";
import type { User } from "firebase/auth";
import { Link } from "react-router-dom";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { ShopApiError, shopAdminApi } from "./shopAdminApi";
import type { StockAffectedOrdersPage } from "./stockAffectedOrdersModel";

export default function StockAffectedOrders({ user, stockDocId, packageName, onClose, onDenied }: {
  user: User; stockDocId: string; packageName: string; onClose: () => void; onDenied: () => void;
}) {
  const [page, setPage] = useState<StockAffectedOrdersPage | null>(null);
  const [trail, setTrail] = useState<(string | null)[]>([null]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const live = useRef(true), busy = useRef(false);
  const failed = useRef<(string | null)[]>([null]);
  const deniedCallback = useRef(onDenied); deniedCallback.current = onDenied;
  const load = async (nextTrail: (string | null)[]) => {
    if (busy.current || !live.current) return;
    busy.current = true; failed.current = nextTrail;
    setLoading(true); setError(""); setPage(null);
    try {
      const result = await shopAdminApi.affectedOrders(user, stockDocId, nextTrail[nextTrail.length - 1] || undefined);
      if (!live.current) return;
      setPage(result); setTrail(nextTrail);
    } catch (caught) {
      if (!live.current) return;
      if (caught instanceof ShopApiError && [401, 403].includes(caught.status)) { deniedCallback.current(); return; }
      setError(caught instanceof ShopApiError && caught.status === 404 ? "Lagerposten finns inte längre. Inga berörda ordrar visas." :
        "Berörda ordrar kunde inte hämtas. Försök igen eller hämta första sidan.");
    } finally { busy.current = false; if (live.current) setLoading(false); }
  };
  useEffect(() => {
    live.current = true; void load([null]);
    return () => { live.current = false; };
    // Parent keys this dialog to its user/capabilities/stock context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const close = () => { live.current = false; onClose(); };
  return <Dialog open onClose={close} fullWidth maxWidth="sm">
    <DialogTitle>Berörda ordrar</DialogTitle>
    <DialogContent>
      <p>{packageName}</p>
      <Alert severity="info">Visar högst 20 referenser per sida. Sparade orderkopplingar granskas mot ursprunglig reservation.
        Detta är inte hela återkallelseunderlaget eller ett besked om betalning eller leverans. Saknade indexreferenser kräver separat lagerutredning.</Alert>
      {loading && <p role="status"><CircularProgress size={18} /> Hämtar ordrar…</p>}
      {error && <Alert severity="error">{error}<Button onClick={() => void load(failed.current)} disabled={loading}>Försök igen</Button></Alert>}
      {page && <>
        <p>Hämtat {new Date(page.asOf).toLocaleString("sv-SE", { timeZone: "Europe/Stockholm" })} · Sida {trail.length}</p>
        {page.orders.length === 0 && <p>Inga fler indexerade orderreferenser finns på denna sida.</p>}
        {page.orders.map((order) => <article key={order.orderId}>
          <h3>{order.orderId}</h3>
          <p>{order.account ? `${order.account.kustomEnvironment === "playground" ? "Playground (test)" : "Live"} · MID ${order.account.merchantId}` : "Okänt betalkonto"}
            {order.matchesActiveAccount === false && " · Annat än aktivt konto"}</p>
          <p>Lokala tillstånd: betalning {order.paymentState} · reservation {order.reservationState} · leverans {order.fulfilmentState}</p>
          {order.allocation ? <p>Verifierad ursprunglig tilldelning: {order.allocation.units} basförpackningar · {order.allocation.basePackageId}</p> :
            <Alert severity="warning">Orderkopplingen kan inte styrkas. Inga tilldelade antal antas. Diagnostik: {order.integrityIssues.join(", ")}</Alert>}
          <Button component={Link} to={`/webshop/orders/${encodeURIComponent(order.orderId)}`}>Granska order {order.orderId}</Button>
        </article>)}
      </>}
    </DialogContent>
    <DialogActions sx={{ flexWrap: "wrap", gap: 1, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
      <Button onClick={close}>Stäng</Button>
      <Button onClick={() => void load([null])} disabled={loading}>Hämta första sidan</Button>
      <Button onClick={() => void load(trail.slice(0, -1))} disabled={loading || !page || trail.length < 2}>Föregående sida</Button>
      <Button onClick={() => page?.nextCursor && void load([...trail, page.nextCursor])} disabled={loading || !page?.nextCursor}>Nästa sida</Button>
    </DialogActions>
  </Dialog>;
}
